import { INPUT_SAMPLE_RATE } from '../../shared/config.ts';
import type { TaskKind } from '../../shared/tasks.ts';

const SAMPLES_PER_MS = INPUT_SAMPLE_RATE / 1000;
// RMS (int16 units) above which a 40 ms frame counts as speech. Room noise with AGC sits around 50-200; speech is
// well above 1000. Deliberately low so quiet speakers still start the recording.
export const VOICE_RMS = 400;
// The least speech that counts as an answer. A read-aloud script can be one short sentence; free speech is meant to
// run 30-60 s, so under 10 s is a false start, and too little for the scorer to judge fluency.
export const MIN_VOICED_MS: Record<TaskKind, number> = {
  interview_answer: 3000,
  free_speech: 10_000,
  read_aloud: 1000,
};
export const MAX_ANSWER_MS = 120_000;

/**
 * Records one spoken answer for a speaking_task. Starts at the first voiced frame after the model stops talking
 * (frames that arrive while the model is speaking are dropped, so the question's echo never lands in the clip), and
 * ends on Done or at the length cap.
 */
export class AnswerRecorder {
  state: 'waiting' | 'recording' = 'waiting';
  voicedMs = 0;
  totalMs = 0;
  private frames: Int16Array[] = [];
  private readonly onCap: () => void;
  private readonly minVoicedMs: number;
  private capped = false;

  constructor(onCap: () => void, minVoicedMs = MIN_VOICED_MS.interview_answer) {
    this.onCap = onCap;
    this.minVoicedMs = minVoicedMs;
  }

  push(frame: Int16Array, modelSpeaking: boolean) {
    if (modelSpeaking || this.capped) return;
    let sum = 0;
    for (let i = 0; i < frame.length; i++) sum += frame[i]! * frame[i]!;
    const voiced = Math.sqrt(sum / Math.max(1, frame.length)) > VOICE_RMS;
    if (this.state === 'waiting') {
      if (!voiced) return;
      this.state = 'recording';
    }
    const ms = frame.length / SAMPLES_PER_MS;
    this.frames.push(frame);
    this.totalMs += ms;
    if (voiced) this.voicedMs += ms;
    if (this.totalMs >= MAX_ANSWER_MS) {
      this.capped = true;
      this.onCap();
    }
  }

  get longEnough() {
    return this.voicedMs >= this.minVoicedMs;
  }

  get atCap() {
    return this.capped;
  }

  /** Keeps what was recorded and stops appending until the next voiced frame, so a pause to read the card isn't kept. */
  pauseUntilVoice() {
    this.state = 'waiting';
  }

  reset() {
    this.frames = [];
    this.state = 'waiting';
    this.voicedMs = 0;
    this.totalMs = 0;
    this.capped = false;
  }

  pcm(): Int16Array {
    const out = new Int16Array(this.frames.reduce((n, f) => n + f.length, 0));
    let o = 0;
    for (const f of this.frames) {
      out.set(f, o);
      o += f.length;
    }
    return out;
  }
}

/** 16-bit mono PCM -> WAV bytes (what /api/answers and Stimuler's speech analysis accept). */
export function encodeWav(pcm: Int16Array, sampleRate = INPUT_SAMPLE_RATE): Uint8Array {
  const bytes = new Uint8Array(44 + pcm.byteLength);
  const v = new DataView(bytes.buffer);
  const tag = (o: number, t: string) => {
    for (let i = 0; i < 4; i++) v.setUint8(o + i, t.charCodeAt(i));
  };
  tag(0, 'RIFF');
  v.setUint32(4, 36 + pcm.byteLength, true);
  tag(8, 'WAVE');
  tag(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  tag(36, 'data');
  v.setUint32(40, pcm.byteLength, true);
  bytes.set(new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength), 44);
  return bytes;
}
